import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const DialogContext = createContext(null);

const contentClass =
  'border border-mlt-border bg-mlt-white dark:bg-mlt-surface text-mlt-text-primary shadow-xl rounded-[var(--mlt-radius)] sm:max-w-md';
const titleClass = 'text-lg font-semibold text-mlt-navy';
const descriptionClass = 'text-sm text-mlt-text-secondary';
const cancelButtonClass =
  'border-mlt-border bg-mlt-white text-mlt-text-primary hover:bg-mlt-off-white';

const confirmButtonClass = {
  default: 'bg-mlt-orange hover:bg-mlt-orange-hover text-white',
  destructive: 'bg-mlt-danger hover:bg-mlt-danger/90 text-white',
  warning: 'bg-mlt-warning hover:bg-mlt-warning/90 text-white',
};

function parseMessage(message) {
  if (!message || typeof message !== 'string') return { title: '', description: '' };
  const parts = message.split('\n\n');
  if (parts.length === 1) return { title: parts[0], description: '' };
  return { title: parts[0], description: parts.slice(1).join('\n\n') };
}

export function DialogProvider({ children }) {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);
  const queueRef = useRef([]);
  const processingRef = useRef(false);

  const processQueue = useCallback(() => {
    if (processingRef.current || queueRef.current.length === 0) return;
    processingRef.current = true;
    const next = queueRef.current.shift();
    setState(next);
  }, []);

  const enqueue = useCallback(
    (entry) =>
      new Promise((resolve) => {
        queueRef.current.push({ ...entry, resolve });
        processQueue();
      }),
    [processQueue]
  );

  const close = useCallback((result) => {
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
    setState(null);
    processingRef.current = false;
    setTimeout(processQueue, 0);
  }, [processQueue]);

  const confirm = useCallback(
    (options = {}) => {
      const parsed =
        options.message && !options.title
          ? parseMessage(options.message)
          : { title: options.title || '', description: options.description || '' };
      return enqueue({
        type: 'confirm',
        title: parsed.title,
        description: parsed.description,
        variant: options.variant || 'default',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
      });
    },
    [enqueue]
  );

  const alertDialog = useCallback(
    (options = {}) => {
      const parsed =
        options.message && !options.title
          ? parseMessage(options.message)
          : { title: options.title || '', description: options.description || '' };
      return enqueue({
        type: 'alert',
        title: parsed.title,
        description: parsed.description,
        copyable: options.copyable || false,
        confirmLabel: options.confirmLabel || 'OK',
      });
    },
    [enqueue]
  );

  const prompt = useCallback(
    (options = {}) => {
      const parsed =
        options.message && !options.title
          ? parseMessage(options.message)
          : { title: options.title || '', description: options.description || '' };
      return enqueue({
        type: 'prompt',
        title: parsed.title,
        description: parsed.description,
        placeholder: options.placeholder || '',
        defaultValue: options.defaultValue || '',
        required: options.required ?? false,
        inputType: options.inputType || 'text',
        validate: options.validate || null,   // (value) => errorString | null
        confirmLabel: options.confirmLabel || 'Continue',
        cancelLabel: options.cancelLabel || 'Cancel',
      });
    },
    [enqueue]
  );

  React.useEffect(() => {
    if (state?.resolve) {
      resolveRef.current = state.resolve;
    }
  }, [state]);

  const handleConfirm = (value) => close(value);

  const [promptValue, setPromptValue] = useState('');
  const [promptError, setPromptError] = useState('');
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (state?.type === 'prompt') {
      setPromptValue(state.defaultValue || '');
      setPromptError('');
    }
    if (state?.type === 'alert') {
      setCopied(false);
    }
  }, [state]);

  const handleCopy = async () => {
    if (!state?.description) return;
    try {
      await navigator.clipboard.writeText(state.description);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const submitPrompt = () => {
    const trimmed = promptValue.trim();
    if (state.required && !trimmed) {
      setPromptError('This field is required.');
      return;
    }
    if (state.validate) {
      const err = state.validate(trimmed);
      if (err) {
        setPromptError(err);
        return;
      }
    }
    handleConfirm(trimmed);
  };

  const value = { confirm, alert: alertDialog, prompt };

  const variant = state?.variant || 'default';
  const isAlert = state?.type === 'alert';
  const isConfirm = state?.type === 'confirm';
  const isPrompt = state?.type === 'prompt';

  return (
    <DialogContext.Provider value={value}>
      {children}

      {(isConfirm || isAlert) && state && (
        <AlertDialog open onOpenChange={(open) => !open && close(isAlert ? undefined : false)}>
          <AlertDialogContent className={contentClass}>
            <AlertDialogHeader>
              <div className="flex items-start gap-3">
                {variant === 'warning' && (
                  <AlertTriangle className="h-5 w-5 shrink-0 text-mlt-warning mt-0.5" />
                )}
                <div className="flex-1 space-y-2">
                  <AlertDialogTitle className={cn(titleClass, '!text-mlt-navy')}>{state.title}</AlertDialogTitle>
                  {state.description && (
                    <AlertDialogDescription
                      className={cn(descriptionClass, 'whitespace-pre-line text-left !text-mlt-text-secondary')}
                    >
                      {state.description}
                    </AlertDialogDescription>
                  )}
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              {state.copyable && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-mlt-border mr-auto"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
              )}
              {!isAlert && (
                <AlertDialogCancel
                  className={cancelButtonClass}
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirm(false);
                  }}
                >
                  {state.cancelLabel}
                </AlertDialogCancel>
              )}
              <AlertDialogAction
                className={cn(
                  isAlert ? confirmButtonClass.default : confirmButtonClass[variant]
                )}
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirm(isAlert ? undefined : true);
                }}
              >
                {state.confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {isPrompt && state && (
        <Dialog open onOpenChange={(open) => !open && handleConfirm(null)}>
          <DialogContent
            className={cn(contentClass, '[&>button.absolute]:text-mlt-text-muted')}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              const input = e.currentTarget?.querySelector('input');
              input?.focus();
            }}
          >
            <DialogHeader>
              <DialogTitle className={cn(titleClass, '!text-mlt-navy')}>{state.title}</DialogTitle>
              {state.description && (
                <DialogDescription className={cn(descriptionClass, '!text-mlt-text-secondary')}>
                  {state.description}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="dialog-prompt-input" className="sr-only">
                {state.title}
              </Label>
              <Input
                id="dialog-prompt-input"
                type={state.inputType}
                value={promptValue}
                onChange={(e) => {
                  setPromptValue(e.target.value);
                  if (promptError) setPromptError('');
                }}
                placeholder={state.placeholder}
                className="border-mlt-border bg-mlt-white text-mlt-text-primary"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitPrompt();
                  }
                }}
              />
              {promptError && (
                <p className="text-sm text-mlt-danger">{promptError}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className={cancelButtonClass}
                onClick={() => handleConfirm(null)}
              >
                {state.cancelLabel}
              </Button>
              <Button
                type="button"
                className={confirmButtonClass.default}
                onClick={submitPrompt}
              >
                {state.confirmLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </DialogContext.Provider>
  );
}

export function useDialogContext() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return ctx;
}
